import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { useAuth } from "../context/AdminContext";

const AdminModal = ({ isOpen, onClose, admin, onSave, isLoading }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const {user} = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Check if the admin is editing their own profile
  const isEditingSelf = admin && user && admin.id === user.id;

  useEffect(() => {
    if (admin) {
      reset({
        username: admin.username || "react-hook-form",
        role: admin.role || "admin",
      });
    } else {
      reset({
        username: "",
        role: "admin",
        password: "",
        confirmPassword: "",
      });
    }
  }, [admin, reset, isOpen]);

  const onSubmit = async (data) => {
    if (!admin && data.password !== data.confirmPassword) {
      Swal.fire({
        title: "Error!",
        text: "Passwords do not match",
        icon: "error",
        confirmButtonColor: "#dc2626",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const adminData = { ...data };
      delete adminData.confirmPassword;
      if (admin && !adminData.password) {
        delete adminData.password;
      }
      await onSave(adminData);
      onClose();
    } catch (error) {
      console.error("Error saving admin:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {admin ? "Edit Admin" : "Add New Admin"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={isLoading || isSubmitting}
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700"
              >
                Username *
              </label>
              <input
                type="text"
                id="username"
                {...register("username", { required: "Username is required" })}
                className={`mt-1 block w-full border  rounded-md px-3 py-2 focus:outline-none  ${
                  errors.username ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isLoading || isSubmitting}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.username.message}
                </p>
              )}
            </div>

            {!admin && (
              <>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    className={`mt-1 block w-full border  rounded-md px-3 py-2 focus:outline-none  ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    }`}
                    disabled={isLoading || isSubmitting}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                    })}
                    className={`mt-1 block w-full border  rounded-md px-3 py-2 focus:outline-none ${
                      errors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    disabled={isLoading || isSubmitting}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700"
              >
                Role *
              </label>
              <select
                id="role"
                {...register("role", { required: "Role is required" })}
                className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none ${
                  errors.role ? "border-red-500" : "border-gray-300"
                } ${isEditingSelf ? "bg-gray-100 cursor-not-allowed" : ""}`}
                disabled={isLoading || isSubmitting || isEditingSelf}
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
              {isEditingSelf && (
                <p className="mt-1 text-sm text-gray-500">
                  You cannot change your own role
                </p>
              )}
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.role.message}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isLoading || isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                disabled={isLoading || isSubmitting}
              >
                {isLoading || isSubmitting
                  ? "Saving..."
                  : admin
                  ? "Update Admin"
                  : "Add Admin"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminModal;
